import { useState, useMemo } from "react";
import { ChevronDown, Calendar, X } from "lucide-react";
import { DayPicker } from "react-day-picker";
import { ptBR } from "date-fns/locale";
import { format } from "date-fns";
import "react-day-picker/dist/style.css";

import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

// FIN-2 — PeriodFilterDropdown.
// 8 presets + calendar custom range (react-day-picker pt-BR).
//
// Estado é controlado pelo parent: filters.period + filters.startDate +
// filters.endDate. Quando preset selecionado, startDate/endDate ficam
// limpos. Quando custom, period vai pra "all" e startDate/endDate
// ditam o range.

const PRESETS = [
  { value: "current_month",  label: "Mês atual" },
  { value: "previous_month", label: "Mês passado" },
  { value: "last_30",        label: "Últimos 30 dias" },
  { value: "last_90",        label: "Últimos 90 dias" },
  { value: "last_6_months",  label: "Últimos 6 meses" },
  { value: "current_year",   label: "Ano atual" },
  { value: "previous_year",  label: "Ano passado" },
  { value: "all",            label: "Tudo" },
];

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return format(new Date(iso + "T00:00:00"), "dd/MM/yyyy", { locale: ptBR });
  } catch {
    return iso;
  }
};

export default function PeriodFilterDropdown({
  filters,
  setManyFilters,
}) {
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState(() => {
    if (filters.startDate && filters.endDate) {
      return {
        from: new Date(filters.startDate + "T00:00:00"),
        to: new Date(filters.endDate + "T00:00:00"),
      };
    }
    return undefined;
  });

  const isCustom = Boolean(filters.startDate && filters.endDate);
  const triggerLabel = useMemo(() => {
    if (isCustom) {
      return `${fmtDate(filters.startDate)} → ${fmtDate(filters.endDate)}`;
    }
    const p = PRESETS.find(x => x.value === (filters.period || "current_month"));
    return p ? p.label : "Período";
  }, [filters.period, filters.startDate, filters.endDate, isCustom]);

  const isDefault = !isCustom && (filters.period === "current_month" || !filters.period);

  const choosePreset = (value) => {
    setManyFilters({ period: value, startDate: "", endDate: "" });
    setRange(undefined);
    setOpen(false);
  };

  const onRangeSelect = (selected) => {
    setRange(selected);
    if (selected?.from && selected?.to) {
      const start = format(selected.from, "yyyy-MM-dd");
      const end = format(selected.to, "yyyy-MM-dd");
      setManyFilters({ period: "all", startDate: start, endDate: end });
      setOpen(false);
    }
  };

  const clearCustom = (e) => {
    e.stopPropagation();
    setManyFilters({ period: "current_month", startDate: "", endDate: "" });
    setRange(undefined);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-caption font-medium border transition-all duration-fast ease-smooth",
            !isDefault
              ? "bg-brand-cyan/10 border-brand-cyan/30 text-brand-cyan"
              : "bg-transparent border-subtle text-foreground-muted hover:bg-surface-card-hover hover:text-foreground",
          )}
          data-testid="extrato-period-trigger"
        >
          <Calendar className="w-3.5 h-3.5" aria-hidden="true" />
          <span className="truncate max-w-[160px]">{triggerLabel}</span>
          {!isDefault ? (
            <span
              role="button"
              tabIndex={0}
              onClick={clearCustom}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") clearCustom(e); }}
              className="inline-flex items-center justify-center rounded-full hover:bg-brand-cyan/20 transition-colors w-4 h-4"
              aria-label="Limpar período"
            >
              <X className="w-3 h-3" />
            </span>
          ) : (
            <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" />
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={6}
        className="p-0 bg-surface-overlay border border-subtle w-auto"
        data-testid="extrato-period-popover"
      >
        <div className="grid grid-cols-1 md:grid-cols-[180px_auto] divide-y md:divide-y-0 md:divide-x divide-subtle">
          {/* Presets */}
          <div className="py-1">
            {PRESETS.map(p => {
              const isActive = (filters.period === p.value) && !isCustom;
              return (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => choosePreset(p.value)}
                  className={cn(
                    "w-full text-left px-3 py-2 text-caption transition-colors",
                    isActive
                      ? "bg-brand-cyan/10 text-brand-cyan font-medium"
                      : "text-foreground-muted hover:bg-surface-card-hover hover:text-foreground",
                  )}
                  data-testid={`extrato-period-preset-${p.value}`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>

          {/* Calendar */}
          <div className="p-3">
            <DayPicker
              mode="range"
              selected={range}
              onSelect={onRangeSelect}
              locale={ptBR}
              showOutsideDays
              className="[&_.rdp-day_selected]:bg-brand-cyan [&_.rdp-day_selected]:text-surface-canvas [&_.rdp-day]:rounded-md"
            />
            <p className="text-micro text-foreground-subtle mt-2">
              Selecione um intervalo para filtro custom.
            </p>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
